import React, { ReactNode } from "react";
import { DataObserveKey, VisualComponent, TooltipProps } from "@siteimprove/fancylib";
import { Popover, DropdownButtonProps } from "../../overlay/popover/popover";
import {
	ActionList,
	ActionListContext,
	ActionListValues,
	ActionListItem,
	divider,
	sectionHeader,
} from "../../tables-and-lists/action-list/action-list";
import { assignKnobName } from "../../../utils/documentation-utils";
import * as scss from "./action-menu.scss";

export type ActionMenuProps = (
	| {
			/** Content to be displayed inside popover */
			children: ReactNode;
	  }
	| (ActionListValues & {
			/** Label to describe just the list inside the popover content */
			listAriaLabel?: string;
	  })
) &
	ActionMenuPopoverProps &
	DataObserveKey &
	VisualComponent;

type ActionMenuPopoverProps = {
	/** Label to describe the dropdown button and the list inside the popover content */
	"aria-label"?: string;
	/** Text and other content for menu menu */
	buttonContent: ReactNode;
	/** Props to affect the styling of the menu button */
	buttonProps?: DropdownButtonProps;
	/** Tooltip text */
	tooltip?: TooltipProps["content"];
	/** Tooltip placement */
	tooltipPlacement?: TooltipProps["placement"];
	/** If set to true, chevron icon will be hidden */
	hideChevron?: boolean;
	/** Classname for container of both menu and popover */
	containerClassName?: string;
};

export type ActionMenuItem = ActionListItem;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasChildren(props: any): props is { children: ReactNode } {
	return props.children !== undefined;
}

function InternalActionMenu(props: ActionMenuProps): JSX.Element {
	const {
		buttonContent,
		buttonProps = {},
		tooltip,
		tooltipPlacement,
		hideChevron,
		"aria-label": ariaLabel,
		"data-observe-key": dataObserveKey,
		containerClassName,
		className,
		style,
	} = props;

	return (
		<Popover
			aria-label={ariaLabel}
			data-observe-key={dataObserveKey}
			className={className}
			style={style}
			aria-haspopup="menu"
			data-component="action-menu"
			containerClassName={containerClassName}
			buttonContent={buttonContent}
			buttonProps={buttonProps}
			placement="bottom-end"
			tooltip={tooltip}
			tooltipPlacement={tooltipPlacement}
			hideChevron={hideChevron}
			popoverContent={(id, firstFocusableRef, openState) => (
				<ActionListContext.Provider
					value={{
						id,
						listClassName: scss.actionMenuList,
						firstFocusableRef,
						openState,
					}}
				>
					{hasChildren(props) ? (
						props.children
					) : (
						<ActionList
							aria-label={props.listAriaLabel ?? ariaLabel}
							items={props.items}
							itemRenderer={props.itemRenderer}
							searchable={props.searchable}
							closeOnSelect={props.closeOnSelect === undefined ? true : props.closeOnSelect}
						/>
					)}
				</ActionListContext.Provider>
			)}
		/>
	);
}

assignKnobName(InternalActionMenu);
InternalActionMenu.displayName = "ActionMenu";

export const ActionMenu = Object.assign(InternalActionMenu, { divider, sectionHeader });
